/**
 * The communication ladder: chirps -> motifs -> glyphs -> broken words ->
 * fluent-ish. Stage is a monotonic ratchet driven by a complexity score
 * (population, lived interaction, trust) — it blends (a stage-3 throng
 * still mostly speaks glyphs) and never regresses.
 */
export const STAGE_THRESHOLDS = [9, 13, 17.5, 22] as const;

export function complexityScore(pop: number, interactions: number, trust: number): number {
  return (
    3 * Math.log2(1 + pop) +
    1.5 * Math.log2(1 + interactions / 40) +
    4 * trust
  );
}

export function stageFor(pop: number, interactions: number, trust: number): number {
  const x = complexityScore(pop, interactions, trust);
  let stage = 0;
  for (let s = 0; s < STAGE_THRESHOLDS.length; s++) {
    if (x >= (STAGE_THRESHOLDS[s] as number)) stage = s + 1;
  }
  if (stage === 4 && trust < 0.55) stage = 3; // fluency needs a bond
  return stage;
}

/** What an utterance is about — content is rendered main-side via lexicon. */
export const INTENT = {
  AMBIENT: 0,
  GREET: 1,
  FED: 2,
  PLAY: 3,
  CLEAN: 4,
  WATCH: 5, // they are watching you
  SLEEPY: 6,
  GRATEFUL: 7,
} as const;

export type Intent = (typeof INTENT)[keyof typeof INTENT];

export function packUtter(stage: number, intent: number): number {
  return stage * 16 + intent;
}

export function unpackUtter(packed: number): { stage: number; intent: number } {
  return { stage: (packed / 16) | 0, intent: packed % 16 };
}
